import { useNavigate } from "@tanstack/react-router";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import {
  type BoardPageEffect,
  createBoardPageMediator,
  isBoardPageEvent,
} from "@/features/board/boardPageMediator";
import { useBoardListPage } from "@/features/board/hooks/useBoardListPage";
import {
  LIST_PAGE_LIMIT,
  useBoardThreadTails,
} from "@/features/board/hooks/useBoardThreads";
import { useFilteredThreads } from "@/features/catalog/hooks/useFilteredThreads";
import { useThreads } from "@/features/catalog/hooks/useThreads";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import type { DesktopHistorySidebarHandle } from "@/features/history/components/DesktopHistorySidebar";
import { useSettingsStore } from "@/features/settings/hooks";
import { useOpenPanelOnFileDrag } from "@/features/thread/hooks/useOpenPanelOnFileDrag";
import { useThreadCreateModalStore } from "@/features/thread/stores/threadCreateModalStore";
import { useIsDesktop } from "@/shared/hooks";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useManualRefresh } from "@/shared/hooks/useManualRefresh";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
export function useBoardPagePresenter() {
  useDocumentTitle(`リストモード - ${BOARD_DISPLAY_NAME}`);
  const currentSort = useCatalogStore((state) => state.currentSort);
  const selectSortMethod = useCatalogStore((state) => state.selectSortMethod);
  const autoReload = useCatalogStore((state) => state.autoReload);
  const setAutoReload = useCatalogStore((state) => state.setAutoReload);
  const openInNewTab = useCatalogStore((state) => state.openInNewTab);
  const isThreadCreateOpen = useThreadCreateModalStore((state) => state.isOpen);
  const openThreadCreate = useThreadCreateModalStore((state) => state.open);
  const closeThreadCreate = useThreadCreateModalStore((state) => state.close);
  const showR18 = useSettingsStore((state) => state.showR18);
  const desktopThreadCreateButtonRef = useRef<HTMLButtonElement>(null);
  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  const navigate = useNavigate();
  // リストモードのページはカタログモードのcatalogStore.pageと共有しない
  // (共有すると片方のページ送りがもう片方に持ち越されるため)。sortだけは
  // モード間で並び順を保つ意図で共有を維持する(#42レビュー指摘)。
  // カタログとは独立したページ番号を維持しつつ、並び順・検索・タグ条件が
  // 変わった場合は、絞り込み前のページ番号を持ち越さず1ページ目へ戻す。
  const [page, setPage] = useBoardListPage(currentSort);
  const { data, isLoading, isFetching, error, refetch } = useThreads({
    page,
    limit: LIST_PAGE_LIMIT,
  });
  const isDesktop = useIsDesktop();
  const { isManualRefreshing, refreshManually } = useManualRefresh(
    refetch,
    isFetching,
  );

  const closeDesktopThreadCreatePanel = useCallback((): void => {
    closeThreadCreate();
    window.requestAnimationFrame(() => {
      desktopThreadCreateButtonRef.current?.focus();
    });
  }, [closeThreadCreate]);

  const threads = useFilteredThreads(data?.threads);
  const tails = useBoardThreadTails(threads.map((thread) => thread.id));

  const effectRef = useRef<(effect: BoardPageEffect) => void>(() => {});
  useLayoutEffect(() => {
    effectRef.current = (event): void => {
      switch (event.type) {
        case "catalog/sort-selected":
          selectSortMethod(event.sort);
          break;
        case "catalog/page-selected":
          setPage(event.page);
          break;
        case "catalog/auto-reload-changed":
          setAutoReload(event.enabled);
          break;
        case "catalog/refresh-requested":
          void refreshManually();
          break;
        case "catalog/retry-requested":
          void refetch();
          break;
        case "catalog/sidebar-toggle-requested":
          desktopHistorySidebarRef.current?.toggle();
          break;
        case "catalog/scroll-requested":
          window.scrollTo({
            top:
              event.edge === "top" ? 0 : document.documentElement.scrollHeight,
          });
          break;
        case "catalog/thread-create-open-requested":
          openThreadCreate();
          break;
        case "catalog/thread-create-close-requested":
          if (isDesktop) closeDesktopThreadCreatePanel();
          else closeThreadCreate();
          break;
        case "board/catalog-requested":
          void navigate({ to: "/" });
          break;
      }
    };
  });
  const [mediator] = useState(() =>
    createBoardPageMediator(
      isDesktop,
      (effect) => effectRef.current(effect),
      () => useThreadCreateModalStore.getState().isOpen,
    ),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) =>
    isBoardPageEvent(event) && mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled",
  );
  const pageState = useMediatorSnapshot(mediator);
  useLayoutEffect(
    () => () => {
      node.dispatch({ type: "catalog/thread-create-close-requested" });
    },
    [node],
  );
  useLayoutEffect(() => {
    mediator.dispatch({
      type: "catalog/page-model-synchronized",
      isDesktop,
      isThreadCreateOpen,
      isFetching,
      hasNextPage: data?.pagination.hasNextPage ?? false,
    });
  }, [
    mediator,
    isDesktop,
    isThreadCreateOpen,
    isFetching,
    data?.pagination.hasNextPage,
  ]);
  const openThreadCreateOnFileDrag = useOpenPanelOnFileDrag(
    isThreadCreateOpen,
    () => {
      node.dispatch({ type: "catalog/thread-create-open-requested" });
    },
  );
  return {
    node,
    ...pageState,
    currentSort,
    autoReload,
    openInNewTab,
    isThreadCreateOpen,
    showR18,
    desktopThreadCreateButtonRef,
    desktopHistorySidebarRef,
    page,
    data,
    isLoading,
    isFetching,
    error,
    isManualRefreshing,
    threads,
    tails,
    openThreadCreateOnFileDrag,
  };
}
